import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Printer, Ban, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";
import { printFreelanceInvoices } from "../lib/freelanceInvoice";

const ACT = { issue: ["إصدار", "text-emerald-700"], reprint: ["إعادة طباعة", "text-slate-600"], edit: ["تعديل مبالغ", "text-amber-700"], cancel: ["إلغاء", "text-rose-700"] };

const HistoryPop = ({ r }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" data-testid={`fl-log-history-${r.id}`}><History className="h-3.5 w-3.5" /> {r.history?.length || 0}</Button>
    </PopoverTrigger>
    <PopoverContent dir="rtl" align="end" className="w-80 p-2">
      <p className="mb-2 text-xs font-bold">سجل التعديلات — {r.number}</p>
      <div className="max-h-64 space-y-1.5 overflow-y-auto">
        {[...(r.history || [])].reverse().map((h, i) => (
          <div key={i} className="rounded-lg bg-slate-50 p-2 text-[11px]">
            <p><b className={ACT[h.action]?.[1]}>{ACT[h.action]?.[0] || h.action}</b> · {h.by} · <span dir="ltr">{(h.at || "").slice(0, 16).replace("T", " ")}</span></p>
            {h.note && <p className="text-slate-500">{h.note}</p>}
          </div>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

const CancelPop = ({ r, onDone }) => {
  const [reason, setReason] = useState(""), [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.cancelFreelanceInvoice(r.id, reason); toast.success(`أُلغيت الفاتورة ${r.number}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإلغاء"); } finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" title="إلغاء الفاتورة" data-testid={`fl-log-cancel-${r.id}`}><Ban className="h-3.5 w-3.5" /></Button></PopoverTrigger>
      <PopoverContent dir="rtl" align="end" className="w-72 space-y-2 p-3">
        <p className="text-xs font-bold">إلغاء الفاتورة {r.number}</p>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الإلغاء (إلزامي)" data-testid={`fl-log-cancel-reason-${r.id}`} />
        <p className="text-[11px] text-slate-500">الرقم يبقى محفوظاً في السجل ولا يُعاد استخدامه. الطباعة القادمة لنفس المندوب والشهر تأخذ رقماً جديداً.</p>
        <Button size="sm" className="w-full bg-rose-600 hover:bg-rose-700" disabled={busy || !reason.trim()} onClick={go} data-testid={`fl-log-cancel-confirm-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تأكيد الإلغاء"}</Button>
      </PopoverContent>
    </Popover>
  );
};

export const FreelanceInvoiceLogDialog = ({ open, onOpenChange, month, fields, company }) => {
  const [m, setM] = useState(month || ""), [q, setQ] = useState(""), [st, setSt] = useState(""), [d, setD] = useState(null);
  useEffect(() => { if (open) setM(month || ""); }, [open, month]);
  const load = useCallback(() => {
    setD(null);
    api.freelanceInvoices({ month: m || undefined, q: q || undefined, status: st || undefined }).then(setD).catch(() => setD({ items: [] }));
  }, [m, q, st]);
  useEffect(() => { if (open) { const t = setTimeout(load, 250); return () => clearTimeout(t); } }, [open, load]);
  const reprint = async (r) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    try {
      const x = await api.reprintFreelanceInvoice(r.id);
      printFreelanceInvoices([{ ...x.snapshot, employee_id: x.employee_id, invoice_number: x.number, cancelled: x.status === "cancelled", cancel_reason: x.cancel_reason }], fields, company, x.month, w);
      load();
    } catch (e) { w.close(); toast.error(e?.response?.data?.detail || "تعذّرت الطباعة"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="w-[96vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="fl-log-dialog">
        <DialogHeader className="text-start">
          <DialogTitle>سجل فواتير الفري لانسر</DialogTitle>
          <DialogDescription className="text-start">كل فاتورة تُطبع تأخذ رقماً تسلسلياً ثابتاً (FL-السنة-0001)، وإعادة طباعتها لنفس المندوب والشهر تبقي نفس الرقم.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={m} onChange={(e) => setM(e.target.value)} className="h-9 w-40" data-testid="fl-log-month" />
          {m && <button className="text-xs text-slate-500 underline" onClick={() => setM("")} data-testid="fl-log-all-months">كل الشهور</button>}
          <div className="relative min-w-[180px] flex-1"><Search className="absolute end-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم الكابتن أو رقم الفاتورة" className="h-9" data-testid="fl-log-search" /></div>
          <select value={st} onChange={(e) => setSt(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="fl-log-status">
            <option value="">كل الحالات</option><option value="active">سارية</option><option value="cancelled">ملغاة</option>
          </select>
        </div>
        {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /> : (<>
          <p className="text-sm" data-testid="fl-log-summary">عدد الفواتير: <b>{d.count || 0}</b> · السارية: <b>{d.active || 0}</b> · إجمالي الصافي المستحق: <b className="text-emerald-700"><Money value={d.total_net || 0} /></b></p>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-right text-xs">
              <thead className="bg-slate-50 text-slate-500"><tr><th className="p-2">رقم الفاتورة</th><th className="p-2">المندوب</th><th className="p-2">الشهر</th><th className="p-2">التطبيق</th><th className="p-2">الصافي</th><th className="p-2">مرات الطباعة</th><th className="p-2">الحالة</th><th className="p-2" /></tr></thead>
              <tbody>
                {d.items.map((r) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${r.status === "cancelled" ? "bg-rose-50/50 text-slate-400" : ""}`} data-testid={`fl-log-row-${r.id}`}>
                    <td className="p-2 font-mono font-bold" dir="ltr">{r.number}</td>
                    <td className="p-2"><p className="font-semibold text-slate-700">{r.employee_name}</p><p className="text-[10px] text-slate-400" dir="ltr">{r.rider_id}</p></td>
                    <td className="p-2">{r.month}</td><td className="p-2">{r.platform || "—"}</td>
                    <td className="p-2 font-bold"><Money value={r.snapshot?.values?.net_salary || 0} /></td>
                    <td className="p-2">{r.print_count}</td>
                    <td className="p-2">{r.status === "cancelled" ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700" title={r.cancel_reason}>ملغاة · {r.cancel_reason}</span> : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">سارية</span>}</td>
                    <td className="whitespace-nowrap p-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-indigo-600" title="إعادة طباعة" onClick={() => reprint(r)} data-testid={`fl-log-reprint-${r.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                      <HistoryPop r={r} />
                      {r.status === "active" && <CancelPop r={r} onDone={load} />}
                    </td>
                  </tr>
                ))}
                {!d.items.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400" data-testid="fl-log-empty">لا توجد فواتير — اطبع فاتورة فري لانسر من جدول الرواتب لتُسجَّل هنا</td></tr>}
              </tbody>
            </table>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
